import orderBy from 'lodash.orderby';
import {
  Building2,
  Contact,
  DollarSignIcon,
  Globe,
  KeySquareIcon,
  LayoutGrid,
  LineChart,
  MessageCircle,
  Network,
  Package,
  PickaxeIcon,
  Receipt,
  ReceiptTextIcon,
  ScaleIcon,
  Settings,
  Shirt,
  TicketsIcon,
  TrophyIcon,
  Tv2Icon,
  UserCircle2,
} from 'lucide-react';

import type { PermissibleRouteKeys } from '@/_pages/settings/user-roles/shared';
import type { RouteListItemWithChildren } from '@/types';

import Agreements from '../../images/icons/agreements.svg?react';
import Competitors from '../../images/icons/competitors.svg?react';
import ConferenceEvents from '../../images/icons/megaphone.svg?react';
import { accountReceivablesSubNav } from '../account-receivables';
import { agreementsSubNav } from '../agreements';
import { companiesSubNav } from '../companies';
import { competitorsSubNav } from '../competitors';
import { conferenceEventsSubNav } from '../conference-events';
import { contactsSubNav } from '../contacts';
import { depositsSubNav } from '../deposits';
import { domainsSubNav } from '../domains';
import { governanceSubNav } from '../governance';
import { inventorySubNav } from '../inventory';
import { marketingCampaignsSubNav } from '../marketing-campaigns';
import { pickTicketsSubNav } from '../pick-tickets';
import { productsAndServicesSubNav } from '../products-and-services';
import { recruitingSubNav } from '../recruiting';
import { settingsSubNav } from '../settings';
import { supportSubNav } from '../support';
import { systemUsersSubNav } from '../system-users';
import { teamMembersSubNav } from '../team-members';

export const mainNavbarRoutesWithChildren = new Map<PermissibleRouteKeys, RouteListItemWithChildren>([
  [
    'dashboard',
    { path: '/dashboard', name: 'Dashboard', icon: LayoutGrid, show: true, auditKey: 'dashboard', sub: [] },
  ],
  [
    'team-member',
    { path: '/team-members', name: 'Team Members', icon: Network, auditKey: 'team-member', sub: teamMembersSubNav },
  ],
  ['client', { path: '/companies', name: 'Companies', icon: Building2, auditKey: 'client', sub: companiesSubNav }],
  ['contact', { path: '/contacts', name: 'Contacts', icon: Contact, auditKey: 'contact', sub: contactsSubNav }],
  [
    'competitor',
    { path: '/competitors', name: 'Competitors', icon: Competitors, auditKey: 'competitor', sub: competitorsSubNav },
  ],
  ['domain', { path: '/domains', name: 'Domains', icon: Globe, auditKey: 'domain', sub: domainsSubNav }],
  [
    'product',
    {
      path: '/products-and-services',
      name: 'Product & Services',
      icon: Package,
      auditKey: 'product',
      sub: productsAndServicesSubNav,
    },
  ],
  [
    'agreement',
    {
      path: '/agreements',
      name: 'Agreements',
      icon: Agreements,
      auditKey: 'agreement',
      sub: [
        ...agreementsSubNav,
        // Virtual permission tab - DO NOT add to `agreementsSubNav` to avoid showing it in the Agreement tabs UI.
        {
          path: '/agreements/$id/financial-override',
          name: 'Financial Override',
          auditKey: 'agreement/financial-override',
        },
      ],
    },
  ],
  ['inventory', { path: '/inventory', name: 'Inventory', icon: Shirt, auditKey: 'inventory', sub: inventorySubNav }],
  [
    'pick-ticket',
    { path: '/pick-tickets', name: 'Pick Tickets', icon: TicketsIcon, auditKey: 'pick-ticket', sub: pickTicketsSubNav },
  ],
  [
    'governance',
    { path: '/governance', name: 'Governance', icon: ScaleIcon, auditKey: 'governance', sub: governanceSubNav },
  ],
  [
    'recruiting',
    { path: '/recruiting', name: 'Recruiting', icon: PickaxeIcon, auditKey: 'recruiting', sub: recruitingSubNav },
  ],
  [
    'marketing-campaign',
    {
      path: '/marketing-campaigns',
      name: 'Marketing Campaigns',
      icon: Tv2Icon,
      auditKey: 'marketing-campaign',
      sub: marketingCampaignsSubNav,
    },
  ],
  [
    'conference-event',
    {
      path: '/conferences-and-events',
      name: 'Conferences & Events',
      icon: ConferenceEvents,
      auditKey: 'conference-event',
      sub: conferenceEventsSubNav,
    },
  ],
  [
    'financial-snapshot',
    {
      path: '/financial-snapshots',
      name: 'Financial Snapshots',
      icon: LineChart,
      auditKey: 'financial-snapshot',
      sub: [],
    },
  ],
  ['award', { path: '/awards', name: 'Awards', icon: TrophyIcon, auditKey: 'award', sub: [] }],
  ['invoice', { path: '/invoices', name: 'Invoices', icon: ReceiptTextIcon, auditKey: 'invoice', sub: [] }],
  [
    'api-integration',
    { path: '/connected-apps', name: 'Connected Apps', icon: KeySquareIcon, auditKey: 'api-integration', sub: [] },
  ],
  [
    'user',
    { path: '/system-users', name: 'System Users', icon: UserCircle2, auditKey: 'user', sub: systemUsersSubNav },
  ],
  ['support', { path: '/support', name: 'Support', icon: MessageCircle, auditKey: 'support', sub: supportSubNav }],
  [
    'account-receivables',
    {
      path: '/account-receivables',
      name: 'Accounts Receivable',
      icon: DollarSignIcon,
      auditKey: 'account-receivables',
      sub: accountReceivablesSubNav,
    },
  ],

  ['deposit', { path: '/deposits', name: 'Deposits', icon: Receipt, auditKey: 'deposit', sub: depositsSubNav }],
  ['settings', { path: '/settings', name: 'Settings', icon: Settings, auditKey: 'settings', sub: settingsSubNav }],
]);

function getMainNavbarRoutesArray() {
  return orderBy(
    Array.from(mainNavbarRoutesWithChildren, ([_, item]) => ({ ...item })),
    'name',
    'asc'
  );
}

export const permissionsRouteTree = getMainNavbarRoutesArray();
